/* eslint-disable */
import type { FormBlockerElementDividerElement } from "../form_blocker.pb";

/** Blocker used to show a Short Form Disclosure to the customer. */
export interface DisclosureBlocker {
  /** The title text displayed at the top of the screen. */
  headline_text?: string;
  /** A list of key fees displayed prominently beneath the headline. */
  line_items?: DisclosureBlockerLineItem[];
  /**
   * The main block of text to display on the screen. This text may include markdown-style links
   * that the client must parse and linkify, as well as markdown-style bold text using asterisks.
   * Example: "By continuing you **agree** to the [Terms](https://cash.app/legal/tos)"
   */
  description_text?: string;
  /** Text to be displayed on the accept button before the screen has reached the bottom. */
  scroll_button_text?: string;
  /** Text to be displayed on the accept button once the screen has been scrolled the bottom. */
  accept_button_text?: string;
  /**
   * Title of the more info panel when it is collapsed.
   * Ignored by client starting in 4.84
   * Old Clients required this field to be non-null
   *
   * @deprecated
   */
  more_info_panel_collapsed_title?: string;
  /**
   * Title of the more info panel when it is expanded.
   * Ignored by client starting in 4.84
   * Old Clients required this field to be non-null
   *
   * @deprecated
   */
  more_info_panel_expanded_title?: string;
  /**
   * Line items shown with Left/Right aligned text.
   * Flattened Ui no longer put into expandable panel starting in 4.84
   */
  more_info_panel_line_items?: DisclosureBlockerLineItem[];
  /**
   * Footer text shown in bold at the bottom of the more info panel when it is expanded.
   * Ignored by client starting in 4.84
   * Old Clients required this field to be non-null
   *
   * @deprecated
   */
  more_info_panel_footer_text?: string;
  /**
   * Footer text shown below the more info section. This text may include markdown-style links that
   * the client must parse and linkify, as well as markdown-style bold text using asterisks.
   * Example: "By continuing you **agree** to the [Terms](https://cash.app/legal/tos)"
   */
  footer_text?: string;
  /** Message to show in a dialog when the customer attempts to cancel out of the blocker. */
  cancel_dialog_text?: string;
  /** Button text for the button to cancel the cancellation dialog. */
  cancel_dialog_cancel_text?: string;
  /** Button text for the button to confirm the cancellation dialog. */
  cancel_dialog_confirm_text?: string;
  /** Indicates whether the legacy fields or structured body_content field should be rendered. */
  body_content_type?: DisclosureBlockerBodyContentType;
  /** Structured body elements displayed in the order provided. */
  body_content?: DisclosureBlockerBodyContent[];
}

export enum DisclosureBlockerBodyContentType {
  /** BODY_CONTENT_TYPE_UNSPECIFIED - The body content type was not specified. */
  BODY_CONTENT_TYPE_UNSPECIFIED = "BODY_CONTENT_TYPE_UNSPECIFIED",
  /** BODY_CONTENT_TYPE_LEGACY - The body is populated using the legacy line item and text fields. */
  BODY_CONTENT_TYPE_LEGACY = "BODY_CONTENT_TYPE_LEGACY",
  /** BODY_CONTENT_TYPE_STRUCTURED - The body is populated using the structured body_content field. */
  BODY_CONTENT_TYPE_STRUCTURED = "BODY_CONTENT_TYPE_STRUCTURED",
}

export interface DisclosureBlockerHorizontalLineItem {
  /** Title displayed above the detail text. */
  title_text?: string;
  /** Detail text displayed below the title. */
  detail_text?: string;
}

export interface DisclosureBlockerHorizontalLineItemGroup {
  /** Items displayed horizontally, each with vertically stacked title and detail text. */
  items?: DisclosureBlockerHorizontalLineItem[];
  /** The minimum text size at which item details may be displayed. */
  detail_text_min_size?: number;
}

export interface DisclosureBlockerVerticalLineItem {
  /** Text displayed in the left column. */
  title_text?: string;
  /** Text displayed in the right column. */
  detail_text?: string;
}

export interface DisclosureBlockerVerticalLineItemGroup {
  /** Items displayed vertically, each with left- and right-aligned text. */
  items?: DisclosureBlockerVerticalLineItem[];
}

export interface DisclosureBlockerTextLineItem {
  /**
   * Text that may include markdown-style links and bold text using asterisks.
   * Example: "By continuing you **agree** to the [Terms](https://cash.app/legal/tos)"
   */
  text?: string;
}

/** A structured element displayed in the body, in the order provided. */
export interface DisclosureBlockerBodyContent {
  horizontal_line_item_group?: DisclosureBlockerHorizontalLineItemGroup | undefined;
  vertical_line_item_group?: DisclosureBlockerVerticalLineItemGroup | undefined;
  text_line_item?: DisclosureBlockerTextLineItem | undefined;
  divider_element?: FormBlockerElementDividerElement | undefined;
}

export interface DisclosureBlockerLineItem {
  /** Text displayed in the left column */
  title_text?: string;
  /** Text displayed in the right column */
  detail_text?: string;
}
